import { useEffect, useState, useRef, useCallback, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEqual, isEmpty, replace, cloneDeep, isUndefined } from "lodash";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { green, grey } from "@mui/material/colors";
import GavelIcon from '@mui/icons-material/Gavel';
import TextField from "@mui/material/TextField";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";

import { isNull } from "lodash/lang";

import rpDemandService from "@plannel/services/rp/rp-demand-service";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import { FilterButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import planVersionService from "@plannel/services/data/plan-version-service";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import NumberUtil from "@plannel/utils/number-util";
const { versionStatusCode, itemType, buckets } = CodeUtil;
const { RELEASED, SCENARIO } = versionStatusCode;
const { snackBarMessageColor } = ColorUtil;

const RpDemand = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);

  const [dpVersion, setDpVersion] = useState(reduxViewState?.dpVersion ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);

  const salesBomLevel = ["salesKit", "component"];
  const EXCEL_EXPORT = "excelExport";
  const [salesBomLevelSearch, setSalesBomLevelSearch] = useState(reduxViewState?.salesBomLevelSearch ?? salesBomLevel[0]);

  const [dpVersions, setDpVersions] = useState([]);
  const [rpDemands, setRpDemands] = useState([]);
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const currentPage = useRef(reduxViewState ? reduxViewState.currentPage : 0);
  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [openFilter, setOpenFilter] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [csvColDef, setCsvColDef] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));
  const defaultGridMemo = useMemo(() => {
    const gridSettings = DefaultGridSetting({ title, viewName });
    gridSettings.defaultColDef.filter = true;

    return gridSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  }

  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.currencyCd
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;


  const columnDefs = [
    { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "locName", field: "locName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
    { headerName: "customerCd", field: "customerCd", hide: false, type: ["nonEditableColumn"], cellClass: 'stringType', filterType: 'string' },
    { headerName: "customerName", field: "customerName", hide: false, type: ["nonEditableColumn"], filterType: 'string' },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], width: 150, valueFormatter: itemValueFormatter, filterType: 'string', filterOption: Object.keys(itemType).filter(key => !["RM", "SFG"].includes(key)).map(key => key.toLowerCase()) },
    { headerName: "dueDate", field: "dueDate", type: ["nonEditableColumn"], width: 100, filterType: 'date' },
    { headerName: "demandQty", field: "demandQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "uomCd", field: "uomCd", type: ["nonEditableColumn"], width: 100, filterType: 'string' },
    { headerName: "demandAmt", field: "demandAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 120 },
    { headerName: "currencyCd", field: "currencyCd", type: ["nonEditableColumn"], width: 150 }
  ];

  const getRequestParams = (isExcelExport) => {
    const params = {};
    const searchFilters = {};

    if (dpVersion) {
      searchFilters.dpVersionId = dpVersion.id;
    }

    if (filterValue?.locations) {
      searchFilters.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (salesBomLevelSearch) {
      searchFilters.salesBomLevel = salesBomLevelSearch;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    searchFilters.isExcelExport = isExcelExport;
    
    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
          params.advancedFilters = advancedFilters;
    }
    
    return params;
  }

  const retrieveRpDemands = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams(false);

    rpDemandService.getAll(params)
      .then(response => {
        const { isLimit, limitSize, results, resultCount } = response.data;
        setRpDemands(results);

        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }

        setResultTotalCount(resultCount);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const filterValue = { dpVersion, salesBomLevelSearch, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, advancedFilters };
        const reduxState = reduxUtil.createObjectState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const sortDpVersions = versions => {
    const releaseVersions = [];
    const scenarioVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        scenarioVersions.push(ver);
      }
    }

    return [...scenarioVersions, ...releaseVersions];
  }

  const retrieveDpVersion = () => {
    planVersionService.getAll({ moduleCd: "DP", statusCds: [RELEASED, SCENARIO], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (results.length > 0) {
          setDpVersion(reduxViewState?.dpVersion || results[0]);
        }
        setDpVersions(sortDpVersions(results));
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = (params) => {
    retrieveDpVersion();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs));
  }

  const onPaginationChanged = useCallback((params) => {
    if (gridRef.current.api) {
      const totalPages = params.api.paginationGetTotalPages();
      if (params.newPage && totalPages > 0) {
        const page = params.api.paginationGetCurrentPage();
        currentPage.current = page;

        const reduxState = reduxUtil.createObjectState(viewName, { currentPage: page });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      }

      if (params.newData && totalPages > 0) {
        gridRef.current.api.paginationGoToPage(currentPage.current);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (limitInfo?.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({ viewName: `${viewName}_NOTIFY`, showLimitSnackbar: false }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo?.isLimit, showLimitSnackbar.current]);

  const initPageToRetrieve = () => {
    if (currentPage.current > 0) {
      currentPage.current = 0;
      gridRef.current.api.paginationGoToPage(currentPage.current);
    }

    retrieveRpDemands();
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
  if (gridLoading && dpVersion) {
      retrieveRpDemands();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading])

  useEffect(() => {
    if (gridLoading && dpVersion) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpVersion?.id, filterValue?.locations, salesBomLevelSearch, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const iconColor = planVersion => {
    if (planVersion) {
      if (isEqual(planVersion.statusCd, RELEASED)) {
        return grey[900];
      }
    }

    return green[600];
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version?.planConfigObj;
      let format = "yyyy-MM";
      if (isEqual(unit, buckets.WEEK)) {
        format = "yyyy-MM-dd";
      }
      return `${dateUtils.formatDate(startDate, format)} ~
      ${dateUtils.formatDate(dateUtils.calculateRangeDate(startDate, unit, startDay, range, true), format)}`
    }
    return null;
  }

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={dpVersions}
          value={!isNull(dpVersion) ? dpVersion : null}
          onChange={(_, newValue) => setDpVersion(newValue)}
          label={`${t("dp")} ${t("version")}`}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          inputIconSx={{ mr: 1 }}
        />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <Box id="rp-demand-sales">
          <Autocomplete
            options={salesBomLevel}
            getOptionLabel={option => t(option)}
            value={salesBomLevelSearch}
            onChange={(_, newValue) => setSalesBomLevelSearch(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            disableClearable
            sx={{ mr: 1, minWidth: 240 }}
            renderInput={(params) => (
              <TextField {...params} label={t("salesBomLevel")} />
            )}
          />
        </Box>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} sx={{ mr: 1, width: 240 }} extraCodesRef={extraCustomerCodesRef} />
        <RPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box id={"rp-demand-filter-icon"}>
          <ExcelExportButton title={title} service={rpDemandService} columnDefs={csvColDef.filter(column => column.suppressColumnsToolPanel !== true)} params={getRequestParams(true)} totalRows={resultTotalCount} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="rp-demand-grid" className="ag-theme-balham grid-area" sx={{ position: 'relative', height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <AgGridReact
              ref={gridRef}
              rowData={rpDemands}
              {...defaultGridMemo}
              pagination
              paginationPageSize={100}
              cacheBlockSize={100}
              onGridReady={onGridReady}
              onPaginationChanged={onPaginationChanged}
              statusBar={statusBar}
            />
          </Box>
          {openFilter && (
            <>
              <ResizableFilterPanel>
                <AdvancedFilter
                  columnDefs={columnDefs}
                  advancedFilters={advancedFilters}
                  onApply={setAdvancedFilters}
                  onClose={() => setOpenFilter(false)}
                />
              </ResizableFilterPanel>
            </>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(RpDemand);